import React from 'react';
import { StyleSheet, TouchableOpacity, ViewStyle } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

import ChevronRight from '@/assets/icons/chevronRight';
import { ReanimatedAccordion } from '@/components/ReanimatedAccordion';
import { Box, Column, Row } from '@/components/lib/components';
import { SEPARATOR_HEIGHT } from '@/styles/styles';

import { ds } from './lib/design.system';

type AccordionProps = {
    header: React.ReactNode;
    content: React.ReactNode;
    initialExpanded?: boolean;
    accordionStyle?: ViewStyle;
    headerStyle?: ViewStyle;
    headerContainerStyle?: ViewStyle;
    contentStyle?: ViewStyle;
    enabled?: boolean;
    accordionExpandedStyle?: ViewStyle;
    accordionCollapsedStyle?: ViewStyle;
    headerActiveOpacity?: number;
    onToggle?: (nextIsExpanded: boolean) => boolean;
};

export const Accordion = ({
    header,
    content,
    initialExpanded,
    accordionStyle = styles.defaultAccordionStyle,
    headerStyle = styles.defaultHeaderStyle,
    headerContainerStyle,
    contentStyle,
    enabled = true,
    accordionExpandedStyle = {},
    accordionCollapsedStyle = {},
    headerActiveOpacity,
    onToggle,
}: AccordionProps) => {
    const expanded = useSharedValue<boolean>(initialExpanded ?? false);

    const toggleExpanded = () => {
        if (!enabled) {
            return;
        }

        expanded.value = !expanded.value;
        onToggle?.(expanded.value);
    };

    const chevronStyle = useAnimatedStyle(() => ({
        transform: [
            {
                rotate: withTiming(expanded.value ? '-90deg' : '90deg', {
                    duration: 200,
                }),
            },
        ],
    }));

    return (
        <Box style={[accordionStyle, expanded.value ? accordionExpandedStyle : accordionCollapsedStyle]}>
            <TouchableOpacity
                onPress={toggleExpanded}
                disabled={!enabled}
                accessible={false}
                activeOpacity={headerActiveOpacity}
                style={headerContainerStyle}
            >
                <Row paddingVertical="s16" style={headerStyle} alignItems="center">
                    <Column flex={1}>{header}</Column>
                    <Column>
                        {enabled ? (
                            <Animated.View style={chevronStyle}>
                                <ChevronRight color={ds.colorValue.icon.secondary} />
                            </Animated.View>
                        ) : null}
                    </Column>
                </Row>
            </TouchableOpacity>
            <ReanimatedAccordion expanded={expanded}>
                <Box style={contentStyle}>{content}</Box>
            </ReanimatedAccordion>
        </Box>
    );
};

const styles = StyleSheet.create({
    defaultAccordionStyle: {
        borderBottomColor: ds.colorValue.border.strong,
        borderBottomWidth: SEPARATOR_HEIGHT,
    },
    defaultHeaderStyle: {
        paddingVertical: 16,
    },
});
